import api from './api';

export interface ExportJob {
  id: string;
  exportType: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  progress: number;
  totalRows: number | null;
  processedRows: number;
  fileName: string | null;
  fileSize: number | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface CreateExportParams {
  exportType: string;
  filters: Record<string, any>;
  columns?: { key: string; header: string }[];
}

/**
 * Create a new export job
 */
export async function createExportJob(params: CreateExportParams): Promise<string> {
  const response = await api.post('/exports', params);
  return response.data.jobId;
}

/**
 * Get export job status
 */
export async function getExportJobStatus(jobId: string): Promise<ExportJob | null> {
  try {
    const response = await api.get(`/exports/${jobId}/status`);
    return response.data.job;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Get user's export history
 */
export async function getExportHistory(limit = 10): Promise<ExportJob[]> {
  const response = await api.get(`/exports/history?limit=${limit}`);
  return response.data.jobs;
}

/**
 * Download export file
 * For Azure storage, uses direct SAS URL. For local storage, downloads via API.
 */
export async function downloadExportFile(jobId: string, fileName: string, downloadUrl?: string | null): Promise<void> {
  // If we have a direct download URL (Azure SAS URL), use it directly
  if (downloadUrl && downloadUrl.startsWith('https://')) {
    // Open the SAS URL in a new window/tab to trigger download
    // This avoids CORS issues with Azure Blob Storage
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  // For local storage or relative URLs, download via API
  const response = await api.get(`/exports/download/${jobId}`, {
    responseType: 'blob',
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Delete export job and its associated file (cleanup)
 */
export async function deleteExportJob(jobId: string): Promise<void> {
  await api.delete(`/exports/${jobId}`);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
